import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { leadService } from '../../services/leadService';

const initialState = {
  leads: [],
  currentLead: null,
  activities: [],
  stats: null,
  loading: false,
  error: null,
  pagination: {
    count: 0,
    next: null,
    previous: null,
  },
};

// Async thunks
export const fetchLeads = createAsyncThunk(
  'leads/fetchLeads',
  async (params = {}, { rejectWithValue }) => {
    try {
      const data = await leadService.getLeads(params);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch leads');
    }
  }
);

export const fetchLead = createAsyncThunk(
  'leads/fetchLead',
  async (id, { rejectWithValue }) => {
    try {
      const data = await leadService.getLead(id);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch lead');
    }
  }
);

export const createLead = createAsyncThunk(
  'leads/createLead',
  async (leadData, { rejectWithValue }) => {
    try {
      const data = await leadService.createLead(leadData);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to create lead');
    }
  }
);

export const updateLead = createAsyncThunk(
  'leads/updateLead',
  async ({ id, leadData }, { rejectWithValue }) => {
    try {
      const data = await leadService.updateLead(id, leadData);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to update lead');
    }
  }
);

export const deleteLead = createAsyncThunk(
  'leads/deleteLead',
  async (id, { rejectWithValue }) => {
    try {
      await leadService.deleteLead(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to delete lead');
    }
  }
);

export const fetchLeadStats = createAsyncThunk(
  'leads/fetchStats',
  async (_, { rejectWithValue }) => {
    try {
      const data = await leadService.getLeadStats();
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch lead stats');
    }
  }
);

const leadSlice = createSlice({
  name: 'leads',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentLead: (state) => {
      state.currentLead = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch leads
      .addCase(fetchLeads.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLeads.fulfilled, (state, action) => {
        state.loading = false;
        state.leads = action.payload.results || action.payload;
        state.pagination = {
          count: action.payload.count || action.payload.length,
          next: action.payload.next || null,
          previous: action.payload.previous || null,
        };
      })
      .addCase(fetchLeads.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch single lead
      .addCase(fetchLead.fulfilled, (state, action) => {
        state.currentLead = action.payload;
      })
      // Create lead
      .addCase(createLead.fulfilled, (state, action) => {
        state.leads.unshift(action.payload);
      })
      // Update lead
      .addCase(updateLead.fulfilled, (state, action) => {
        const index = state.leads.findIndex(lead => lead.id === action.payload.id);
        if (index !== -1) {
          state.leads[index] = action.payload;
        }
        if (state.currentLead?.id === action.payload.id) {
          state.currentLead = action.payload;
        }
      })
      // Delete lead
      .addCase(deleteLead.fulfilled, (state, action) => {
        state.leads = state.leads.filter(lead => lead.id !== action.payload);
      })
      // Fetch stats
      .addCase(fetchLeadStats.fulfilled, (state, action) => {
        state.stats = action.payload;
      });
  },
});

export const { clearError, clearCurrentLead } = leadSlice.actions;
export default leadSlice.reducer;